<template>
  <div class="alice-ins" :class="{ 'alice-ins--readonly': isGuest }">
    <ChapterDialog
      ref="chapterDialog"
      :type="dialogType"
      :dialog="dialog"
      @use="useInsights"
      @close="dialog = false"
    />
    <InsightDialog
      :dialog="slideOpen"
      :insight="slideInsight"
      :project-uid="project?.uid || ''"
      :chapter-title="slideChapterTitle"
      :chapter-index="slideChapterIndex"
      @close="closeSlide"
      @delete="deleteFromSlide"
      @save="saveFromSlide"
    />

    <!-- ============== Sub-page header ============== -->
    <header class="alice-ins__head">
      <div class="alice-ins__head-main">
        <div class="alice-ins__crumb">
          <span>Project</span>
          <Icon name="chevron-right" :size="12" />
          <span>Insights</span>
        </div>
        <h1 class="alice-ins__title">
          Insights
          <span class="alice-ins__count">{{ totalInsights }}</span>
        </h1>
        <p class="alice-ins__subtitle">
          Structureer wat je hebt geleerd. Groepeer bevindingen per hoofdstuk,
          ondersteun met quotes en laat Alice aanbevelingen genereren.
        </p>
      </div>

      <div class="alice-ins__head-actions">
        <button
          class="alice-ins__head-btn"
          :class="{ 'alice-ins__head-btn--active': !draggableDisabled }"
          :title="
            draggableDisabled ? 'Enable reordering' : 'Disable reordering'
          "
          @click="draggableDisabled = !draggableDisabled"
        >
          <Icon name="sort" :size="16" />
          <span>{{ draggableDisabled ? "Reorder" : "Done" }}</span>
        </button>

        <v-menu v-model="menuChapter" :close-on-content-click="false">
          <template #activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              class="alice-ins__head-btn alice-ins__head-btn--primary"
            >
              <Icon name="plus" :size="16" />
              <span>Add chapter</span>
              <Icon name="chevron-down" :size="12" />
            </button>
          </template>
          <v-list class="alice-menu-list">
            <v-list-item @click="addChapter">
              <v-list-item-title>Empty chapter</v-list-item-title>
            </v-list-item>
            <v-list-item @click="askChatGPT('chapter')">
              <v-list-item-title>Ask ChatGPT</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </header>

    <!-- ============== Stats trio ============== -->
    <div class="alice-ins__stats">
      <div class="alice-ins__stat-tile">
        <div class="alice-ins__stat-row">
          <span
            class="alice-ins__stat-mark"
            style="background: var(--vp-black)"
          />
          <span class="alice-ins__stat-value">{{ chapters.length }}</span>
        </div>
        <div class="alice-ins__stat-label">Chapters</div>
        <div class="alice-ins__stat-sub">{{ chaptersSubLabel }}</div>
      </div>

      <div class="alice-ins__stat-tile">
        <div class="alice-ins__stat-row">
          <span
            class="alice-ins__stat-mark"
            style="background: var(--vp-turquoise-100)"
          />
          <span class="alice-ins__stat-value">{{ totalInsights }}</span>
        </div>
        <div class="alice-ins__stat-label">Insights</div>
        <div class="alice-ins__stat-sub">
          {{ totalQuotes }} supporting quotes
        </div>
      </div>

      <div class="alice-ins__stat-tile">
        <div class="alice-ins__stat-row">
          <span
            class="alice-ins__stat-mark"
            style="background: var(--vp-rood-100)"
          />
          <span class="alice-ins__stat-value">{{ totalRecommendations }}</span>
        </div>
        <div class="alice-ins__stat-label">Recommendations</div>
        <div class="alice-ins__stat-sub">{{ recommendationsSubLabel }}</div>
      </div>
    </div>

    <!-- ============== Body ============== -->
    <div v-if="!chapters.length" class="alice-ins__empty">
      <p class="alice-ins__empty-headline">Geen hoofdstukken.</p>
      <p class="alice-ins__empty-body">
        Begin met een leeg hoofdstuk of laat Alice de eerste indeling
        voorstellen op basis van je transcripts.
      </p>
      <div class="alice-ins__empty-actions">
        <button class="alice-ins__head-btn" @click="addChapter">
          <Icon name="plus" :size="16" />
          <span>Empty chapter</span>
        </button>
        <button
          class="alice-ins__head-btn alice-ins__head-btn--primary"
          @click="askChatGPT('chapter')"
        >
          <Icon name="plus" :size="16" />
          <span>Ask ChatGPT</span>
        </button>
      </div>
    </div>

    <draggable
      v-else
      v-model="project.chapters"
      tag="div"
      class="alice-ins__chapters"
      group="chapters"
      :disabled="draggableDisabled || isGuest"
      item-key="uid"
      handle=".alice-ins__chapter-grip"
      @start="drag = true"
      @end="drag = false"
    >
      <template #item="{ element: chapter, index: ci }">
        <article
          class="alice-ins__chapter"
          :class="{ 'is-stagger-in': staggerIn }"
          :style="
            staggerIn ? { animationDelay: Math.min(ci, 7) * 40 + 'ms' } : null
          "
        >
          <header class="alice-ins__chapter-head">
            <span
              v-if="!draggableDisabled"
              class="alice-ins__grip alice-ins__chapter-grip"
              title="Drag to reorder chapter"
            >
              ⋮⋮
            </span>
            <span class="alice-ins__chapter-seq">{{
              String(ci + 1).padStart(2, "0")
            }}</span>
            <input
              v-model="chapter.title"
              class="alice-ins__chapter-title"
              type="text"
              placeholder="Chapter title…"
            />
            <button
              class="alice-ins__icon-btn"
              title="Generate title from insights"
              @click="generateChapterTitle(ci)"
            >
              <Icon name="star" :size="14" />
            </button>
            <button
              class="alice-ins__icon-btn alice-ins__icon-btn--danger"
              title="Delete chapter"
              @click="removeChapter(ci)"
            >
              <Icon name="trash" :size="14" />
            </button>
          </header>

          <div class="alice-ins__chapter-meta">
            <span>
              {{ (chapter.insights || []).length }} insight{{
                (chapter.insights || []).length === 1 ? "" : "s"
              }}
            </span>
            <span>·</span>
            <span>{{ chapterQuoteCount(chapter) }} quotes</span>
            <span v-if="(chapter.recommendations || []).length">·</span>
            <span v-if="(chapter.recommendations || []).length">
              {{ chapter.recommendations.length }} recommendation{{
                chapter.recommendations.length === 1 ? "" : "s"
              }}
            </span>
          </div>

          <draggable
            v-model="chapter.insights"
            class="alice-ins__insight-list"
            group="insights"
            :disabled="draggableDisabled || isGuest"
            item-key="uid"
            handle=".alice-ins__grip"
            @start="drag = true"
            @end="drag = false"
          >
            <template #item="{ element: insight, index: ii }">
              <article
                class="alice-ins__insight"
                :class="`alice-ins__insight--${insight.status || 'draft'}`"
                tabindex="0"
                role="button"
                title="Open as slide"
                @click="openSlide(ci, ii)"
                @keydown.enter.prevent="openSlide(ci, ii)"
              >
                <div class="alice-ins__insight-head">
                  <span
                    v-if="!draggableDisabled"
                    class="alice-ins__grip"
                    title="Drag to reorder"
                    @click.stop
                  >
                    ⋮⋮
                  </span>
                  <span
                    class="alice-ins__insight-mark"
                    :class="`alice-ins__insight-mark--${
                      insight.status || 'draft'
                    }`"
                    :title="statusLabel(insight.status)"
                  />
                  <span v-if="insight.topic" class="alice-ins__insight-topic">{{
                    insight.topic
                  }}</span>
                  <span class="alice-ins__insight-title-line">{{
                    insight.title || "Untitled insight"
                  }}</span>
                  <span class="alice-ins__insight-counts">
                    <span>{{ (insight.findings || []).length }} F</span>
                    <span class="alice-ins__insight-counts-sep">·</span>
                    <span>{{ (insight.quotes || []).length }} Q</span>
                    <template v-if="(insight.images || []).length">
                      <span class="alice-ins__insight-counts-sep">·</span>
                      <span
                        class="alice-ins__insight-images"
                        :title="`${insight.images.length} afbeelding${
                          insight.images.length === 1 ? '' : 'en'
                        }`"
                      >
                        <Icon name="image" :size="11" />
                        {{ insight.images.length }}
                      </span>
                    </template>
                  </span>
                  <button
                    class="alice-ins__copy-btn"
                    :class="{
                      'alice-ins__copy-btn--done': copiedKey === insight.uid,
                    }"
                    type="button"
                    :title="
                      copiedKey === insight.uid ? 'Copied' : 'Copy insight'
                    "
                    @click.stop="copyInsight(insight)"
                  >
                    <Icon name="copy" :size="14" />
                  </button>
                  <button
                    class="alice-ins__icon-btn alice-ins__icon-btn--danger"
                    title="Delete insight"
                    @click.stop="removeInsight(ci, ii)"
                  >
                    <Icon name="trash" :size="14" />
                  </button>
                  <span class="alice-ins__insight-chev"
                    ><Icon name="arrow-right" :size="14"
                  /></span>
                </div>
              </article>
            </template>
          </draggable>

          <div class="alice-ins__add-insight">
            <v-menu
              v-model="chapter._addMenu"
              :close-on-content-click="false"
              location="bottom"
            >
              <template #activator="{ props: menuProps }">
                <button v-bind="menuProps" class="alice-ins__add-btn">
                  <Icon name="plus" :size="14" />
                  <span>Add insight</span>
                  <Icon name="chevron-down" :size="12" />
                </button>
              </template>
              <v-list class="alice-menu-list">
                <v-list-item @click="addInsightToChapter(ci)">
                  <v-list-item-title>Empty insight</v-list-item-title>
                </v-list-item>
                <v-list-item @click="askChatGPT('insight', ci)">
                  <v-list-item-title>Ask ChatGPT</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <details class="alice-ins__recommendations">
            <summary class="alice-ins__rec-head">
              <span class="alice-ins__rec-mark" />
              <span class="alice-ins__rec-title">Recommendations</span>
              <span
                v-if="(chapter.recommendations || []).length"
                class="alice-ins__rec-count"
                >{{ chapter.recommendations.length }}</span
              >
              <button
                class="alice-ins__icon-btn"
                title="Generate recommendations"
                @click.prevent.stop="generateRecommendations(ci)"
              >
                <Icon name="star" :size="14" />
              </button>
              <span class="alice-ins__insight-chev"
                ><Icon name="chevron-down" :size="14"
              /></span>
            </summary>
            <div class="alice-ins__rec-body">
              <label class="alice-ins__field">
                <span class="alice-ins__label">Title</span>
                <input
                  v-model="chapter.recommendations_title"
                  class="alice-ins__input"
                  type="text"
                  placeholder="Recommendations title…"
                />
              </label>

              <div
                v-for="(r, ri) in chapter.recommendations"
                :key="ri"
                class="alice-ins__row"
              >
                <textarea
                  v-model="r.text"
                  class="alice-ins__textarea alice-ins__textarea--mini"
                  rows="3"
                  placeholder="Recommendation…"
                />
                <button
                  class="alice-ins__icon-btn alice-ins__icon-btn--danger alice-ins__row-del"
                  title="Remove recommendation"
                  type="button"
                  @click="chapter.recommendations.splice(ri, 1)"
                >
                  <Icon name="trash" :size="14" />
                </button>
              </div>

              <button
                class="alice-ins__add-btn alice-ins__add-btn--inline"
                type="button"
                @click="addRecommendation(ci)"
              >
                <Icon name="plus" :size="14" />
                <span>Add recommendation</span>
              </button>
            </div>
          </details>
        </article>
      </template>
    </draggable>
  </div>
</template>

<script>
import { mapState } from "vuex";
import draggable from "vuedraggable";
import { functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";

import ChapterDialog from "@/components/ChapterDialog.vue";
import InsightDialog from "@/components/InsightDialog.vue";
import Icon from "@/components/alice/Icon.vue";
import {
  newChapter,
  newInsight,
  INSIGHT_STATUS_LABELS,
  normalizeStatus,
} from "@/utils/insightsModel";
import { insightToClipboardText } from "@/utils/insightsMarkdown";
import { copyFeedbackMixin } from "@/utils/clipboard";

export default {
  name: "ProjectInsights",
  components: { ChapterDialog, InsightDialog, Icon, draggable },
  mixins: [copyFeedbackMixin],
  data() {
    return {
      // Slide drawer (InsightDialog) — the one place an insight is edited.
      slideOpen: false,
      slideInsight: null,
      slideChapterIndex: null,
      slideInsightIndex: null,
      dialog: false,
      dialogType: "chapter",
      dialogTargetChapter: null,
      drag: false,
      draggableDisabled: true,
      language: "Dutch",
      menuChapter: false,
      staggerIn: true,
    };
  },
  mounted() {
    // Let the chapters cascade in once, then stop gating future re-renders.
    this._staggerTimer = setTimeout(() => {
      this.staggerIn = false;
    }, 700);
  },
  beforeUnmount() {
    clearTimeout(this._staggerTimer);
  },
  computed: {
    ...mapState({
      project: (state) => state.project,
    }),
    isGuest() {
      return this.$store.getters.isGuest;
    },
    chapters() {
      return this.project?.chapters || [];
    },
    slideChapterTitle() {
      if (this.slideChapterIndex === null) return "";
      return this.chapters[this.slideChapterIndex]?.title || "";
    },
    totalInsights() {
      return this.chapters.reduce(
        (sum, c) => sum + (c.insights?.length || 0),
        0
      );
    },
    totalQuotes() {
      return this.chapters.reduce(
        (sum, c) =>
          sum +
          (c.insights || []).reduce(
            (qsum, ins) => qsum + (ins.quotes?.length || 0),
            0
          ),
        0
      );
    },
    totalRecommendations() {
      return this.chapters.reduce(
        (sum, c) => sum + (c.recommendations?.length || 0),
        0
      );
    },
    chaptersWithRecommendations() {
      return this.chapters.filter((c) => (c.recommendations || []).length > 0)
        .length;
    },
    chaptersSubLabel() {
      const empty = this.chapters.filter(
        (c) => !(c.insights || []).length
      ).length;
      if (this.chapters.length === 0) return "—";
      if (empty === 0) return "all populated";
      return `${empty} empty`;
    },
    recommendationsSubLabel() {
      if (!this.chapters.length) return "—";
      const without = this.chapters.length - this.chaptersWithRecommendations;
      if (without === 0) return "every chapter covered";
      return `${without} chapter${without === 1 ? "" : "s"} without`;
    },
  },
  methods: {
    copyInsight(insight) {
      this.copyWithFeedback(insight.uid, insightToClipboardText(insight));
    },
    statusLabel(status) {
      return INSIGHT_STATUS_LABELS[normalizeStatus(status)];
    },
    // ----- Slide view -----
    openSlide(ci, ii) {
      const ins = this.project?.chapters?.[ci]?.insights?.[ii];
      if (!ins) return;
      this.slideInsight = ins;
      this.slideChapterIndex = ci;
      this.slideInsightIndex = ii;
      this.slideOpen = true;
    },
    closeSlide() {
      this.slideOpen = false;
      this.slideInsight = null;
      this.slideChapterIndex = null;
      this.slideInsightIndex = null;
    },
    // Opslaan on the slide persists right away (the button says so), which
    // also saves any other pending edits on this page.
    saveFromSlide(draft) {
      if (draft && this.slideInsight) Object.assign(this.slideInsight, draft);
      this.closeSlide();
      this.$store.dispatch("updateProject");
    },
    deleteFromSlide() {
      const ci = this.slideChapterIndex;
      const ii = this.slideInsightIndex;
      const ins = this.project?.chapters?.[ci]?.insights?.[ii];
      if (!ins || ins !== this.slideInsight) return;
      if (!confirm(`Delete "${ins.title || "this insight"}"?`)) return;
      this.project.chapters[ci].insights.splice(ii, 1);
      this.closeSlide();
      this.$store.dispatch("updateProject");
    },
    chapterQuoteCount(chapter) {
      return (chapter.insights || []).reduce(
        (sum, ins) => sum + (ins.quotes?.length || 0),
        0
      );
    },

    addChapter() {
      this.menuChapter = false;
      if (!this.project) return;
      if (!this.project.chapters) this.project.chapters = [];
      // A chapter starts with one empty insight, opened in the slide so the
      // researcher can fill it right away.
      this.project.chapters.push(newChapter({ insights: [newInsight()] }));
      this.openSlide(this.project.chapters.length - 1, 0);
    },
    askChatGPT(type, chapterIndex = null) {
      this.menuChapter = false;
      this.dialogType = type;
      this.dialogTargetChapter = chapterIndex;
      this.dialog = true;
    },
    removeChapter(i) {
      const chapter = this.project.chapters[i];
      const label = chapter?.title || "Untitled chapter";
      if (!confirm(`Delete "${label}"?`)) return;
      this.project.chapters.splice(i, 1);
    },

    addInsightToChapter(i) {
      const chapter = this.project.chapters[i];
      if (!chapter) return;
      chapter._addMenu = false;
      chapter.insights.push(newInsight());
      this.openSlide(i, chapter.insights.length - 1);
    },
    removeInsight(ci, ii) {
      const insight = this.project.chapters[ci]?.insights[ii];
      const label = insight?.title || "this insight";
      if (!confirm(`Delete "${label}"?`)) return;
      this.project.chapters[ci].insights.splice(ii, 1);
    },

    addRecommendation(i) {
      const chapter = this.project.chapters[i];
      if (!chapter.recommendations) chapter.recommendations = [];
      chapter.recommendations.push({ text: "" });
    },

    async generateChapterTitle(i) {
      this.$store.commit("setLoading", true);
      try {
        const generateChapterTitle = httpsCallable(
          functions,
          "generateChapterTitle"
        );
        const insights = (this.project.chapters[i].insights || [])
          .map((ins) => `-${ins.title || ""}`)
          .join("\n");
        const res = await generateChapterTitle({
          insights,
          language: this.language,
        });
        this.project.chapters[i].title = res.data;
      } catch (err) {
        console.error("generateChapterTitle failed:", err);
      } finally {
        this.$store.commit("setLoading", false);
      }
    },
    async generateRecommendations(i) {
      this.$store.commit("setLoading", true);
      try {
        const generateRecommendations = httpsCallable(
          functions,
          "generateRecommendations"
        );
        const res = await generateRecommendations({
          chapter: this.project.chapters[i],
          language: this.language,
        });
        this.project.chapters[i].recommendations = res.data.recommendations;
        this.project.chapters[i].recommendations_title = res.data.title;
      } catch (err) {
        console.error("generateRecommendations failed:", err);
      } finally {
        this.$store.commit("setLoading", false);
      }
    },

    useInsights(insights) {
      // Same merge logic as the previous implementation: group by chapter
      // title from the AI response, append to project.chapters.
      const chapters = [];
      insights.forEach((insight) => {
        const idx = chapters.findIndex((c) => c.title === insight.chapter);
        const formatted = newInsight({
          title: insight.title || "",
          topic: insight.topic || "",
          strength:
            typeof insight.strength === "number" ? insight.strength : null,
          findings: (insight.findings || []).map((f) => ({ text: f })),
          quotes: (insight.quotes || []).map((q) => ({
            text: q,
            selected: false,
          })),
        });
        if (idx >= 0) {
          chapters[idx].insights.push(formatted);
        } else {
          chapters.push(
            newChapter({ title: insight.chapter || "", insights: [formatted] })
          );
        }
      });

      if (this.dialogType === "insight" && this.dialogTargetChapter !== null) {
        // AI generated insights for a specific chapter; merge into that one.
        const target = this.project.chapters[this.dialogTargetChapter];
        if (target) {
          chapters.forEach((c) =>
            c.insights.forEach((ins) => target.insights.push(ins))
          );
        }
      } else {
        chapters.forEach((c) => this.project.chapters.push(c));
      }
      this.dialog = false;
      this.dialogTargetChapter = null;
    },
  },
};
</script>

<style scoped lang="scss">
/* Guest (read-only) mode: hide every edit affordance in one place instead of
   sprinkling v-ifs through the editor. Server rules enforce the real
   boundary — this only keeps the UI honest. */
.alice-ins--readonly .alice-ins__head-actions,
.alice-ins--readonly .alice-ins__empty-actions,
.alice-ins--readonly .alice-ins__icon-btn,
.alice-ins--readonly .alice-ins__inline-btn,
.alice-ins--readonly .alice-ins__add-insight,
.alice-ins--readonly .alice-ins__add-btn,
.alice-ins--readonly .alice-ins__row-del,
.alice-ins--readonly .alice-ins__quote-toggle,
.alice-ins--readonly .alice-ins__grip {
  display: none !important;
}
.alice-ins--readonly textarea,
.alice-ins--readonly input {
  pointer-events: none;
}

.alice-ins {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-ins__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-ins__head-main {
  flex: 1;
  min-width: 0;
}
.alice-ins__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-ins__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-ins__count {
  display: inline-block;
  margin-left: 12px;
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: 700;
  color: var(--fg-3);
  vertical-align: middle;
}
.alice-ins__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 560px;
  line-height: 1.5;
}
.alice-ins__head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.alice-ins__head-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
  border-radius: 0;
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-ins__head-btn:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-ins__head-btn--primary {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-ins__head-btn--primary:hover {
  background: var(--accent-100);
  color: var(--vp-black);
  border-color: var(--accent-100);
}
.alice-ins__head-btn--active {
  background: var(--accent-20);
  border-color: var(--accent-100);
  color: var(--vp-black);
}
.alice-ins__head-btn--active:hover {
  background: var(--accent-100);
  color: var(--vp-black);
  border-color: var(--accent-100);
}

/* ============== Stats ============== */
.alice-ins__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid var(--border-hairline);
}
.alice-ins__stat-tile {
  position: relative;
  padding: 20px 32px;
  min-width: 0;
}
.alice-ins__stat-tile + .alice-ins__stat-tile {
  border-left: 1px solid var(--border-hairline);
}
.alice-ins__stat-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.alice-ins__stat-mark {
  width: 10px;
  height: 10px;
  align-self: center;
  flex-shrink: 0;
}
.alice-ins__stat-value {
  font-family: var(--font-display);
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--vp-black);
}
.alice-ins__stat-label {
  margin-top: 8px;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-ins__stat-sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}

/* ============== Empty ============== */
.alice-ins__empty {
  padding: 64px 32px;
  text-align: center;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-ins__empty-headline {
  margin: 0 0 8px;
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  color: var(--vp-black);
  letter-spacing: -0.01em;
}
.alice-ins__empty-body {
  margin: 0 0 20px;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}
.alice-ins__empty-actions {
  display: inline-flex;
  gap: 8px;
}

/* ============== Chapters ============== */
.alice-ins__chapters {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 32px 48px;
}
.alice-ins__chapter {
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  border-radius: 0;
}

/* Staggered list entrance (decorative, plays once on mount) */
@keyframes alice-ins-fade-in-up {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.alice-ins__chapter.is-stagger-in {
  animation: alice-ins-fade-in-up var(--dur-base) var(--ease-out) both;
}
.alice-ins__chapter-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-ins__chapter-seq {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 28px;
  background: var(--vp-grijs-20);
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--fg-3);
  flex-shrink: 0;
}
.alice-ins__chapter-title {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
}
.alice-ins__chapter-title::placeholder {
  color: var(--fg-3);
  font-weight: 400;
}
.alice-ins__chapter-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px 0;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}

/* Generic icon button */
.alice-ins__icon-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--vp-black);
  border-radius: 0;
  flex-shrink: 0;
  transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease;
}
.alice-ins__icon-btn:hover {
  border-color: var(--vp-black);
}
.alice-ins__icon-btn--danger {
  color: var(--vp-rood-100);
}
.alice-ins__icon-btn--danger:hover {
  border-color: var(--vp-rood-100);
  background: var(--vp-rood-10);
}
/* Copy stays available to guests, so it is not an .alice-ins__icon-btn. */
.alice-ins__copy-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--fg-3);
  border-radius: 0;
  flex-shrink: 0;
  transition: border-color 0.12s ease, color 0.12s ease;
}
.alice-ins__copy-btn:hover {
  border-color: var(--vp-black);
  color: var(--vp-black);
}
.alice-ins__copy-btn--done {
  color: var(--vp-turquoise-100);
  border-color: var(--vp-turquoise-100);
}

/* ============== Insight list ============== */
.alice-ins__insight-list {
  display: flex;
  flex-direction: column;
}
.alice-ins__insight {
  border-top: 1px solid var(--border-hairline);
  transition: background var(--dur-fast) var(--ease-hover);
}
.alice-ins__insight:hover {
  background: var(--bg-muted);
}
.alice-ins__insight:focus-visible {
  outline: 2px solid var(--vp-black);
  outline-offset: -2px;
}
.alice-ins__insight:first-child {
  border-top: none;
}
.alice-ins__insight-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  list-style: none;
  position: relative;
  user-select: none;
}
.alice-ins__insight:hover {
  background: var(--bg-muted);
}
.alice-ins__grip {
  font-family: var(--font-mono);
  font-size: 14px;
  color: var(--fg-3);
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
}
.alice-ins__grip:active {
  cursor: grabbing;
}
.alice-ins__insight-mark {
  width: 8px;
  height: 8px;
  background: var(--vp-black);
  flex-shrink: 0;
}
/* Report status: Klad black, Ter review yellow, Klaar green. */
.alice-ins__insight-mark--review {
  background: var(--vp-geel-100);
}
.alice-ins__insight-mark--done {
  background: var(--vp-groen-100);
}
.alice-ins__insight-topic {
  flex-shrink: 0;
  max-width: 220px;
  padding: 2px 8px;
  background: var(--bg-muted);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-ins__insight-title-line {
  flex: 1;
  min-width: 0;
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 700;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-ins__insight-counts {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--fg-3);
  flex-shrink: 0;
}
.alice-ins__insight-images {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.alice-ins__insight-counts-sep {
  color: var(--vp-grijs-40);
}
.alice-ins__insight-chev {
  display: inline-flex;
  color: var(--fg-3);
  transition: transform 0.15s ease;
  flex-shrink: 0;
}
details[open] > .alice-ins__rec-head .alice-ins__insight-chev {
  transform: rotate(180deg);
}

.alice-ins__insight-body {
  padding: 0 16px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-top: 1px solid var(--border-hairline);
  padding-top: 16px;
  background: var(--bg-muted);
}

/* ============== Form atoms ============== */
.alice-ins__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.alice-ins__label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
}
/* Sub-section (Findings / Quotes / Images) */
.alice-ins__sub {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.alice-ins__sub-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.alice-ins__inline-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vp-black);
  border-radius: 0;
  transition: background 0.12s ease, color 0.12s ease;
}
.alice-ins__inline-btn:hover:not(:disabled) {
  background: var(--vp-black);
  color: var(--vp-white);
}
.alice-ins__inline-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.alice-ins__hint {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  font-style: italic;
}

/* Row pattern for finding / quote / recommendation */
.alice-ins__row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.alice-ins__row-del {
  margin-top: 2px;
}
.alice-ins__row--quote {
  align-items: stretch;
}
.alice-ins__row--quote .alice-ins__row-del {
  margin-top: 0;
  align-self: flex-start;
}
.alice-ins__row--selected .alice-ins__textarea {
  background: var(--accent-20);
  border-color: var(--accent-100);
}
.alice-ins__quote-toggle {
  width: 22px;
  height: 22px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  cursor: pointer;
  border-radius: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 6px;
}
.alice-ins__quote-toggle--on {
  background: var(--vp-black);
}
.alice-ins__quote-tick {
  width: 10px;
  height: 10px;
  background: var(--accent-100);
}
.alice-ins__quote-name {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
  align-self: flex-start;
  margin-top: 8px;
  padding: 3px 8px;
  border: 1px solid var(--border-hairline);
}

/* ============== Add insight ============== */
.alice-ins__add-insight {
  display: flex;
  justify-content: center;
  padding: 12px;
  border-top: 1px solid var(--border-hairline);
}
.alice-ins__add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--vp-white);
  border: 1px dashed var(--vp-grijs-60);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
  border-radius: 0;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.alice-ins__add-btn:hover {
  border-color: var(--vp-black);
  border-style: solid;
  color: var(--vp-black);
}
.alice-ins__add-btn--inline {
  align-self: flex-start;
}

/* ============== Recommendations ============== */
.alice-ins__recommendations {
  border-top: 1px solid var(--border-hairline);
}
.alice-ins__rec-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  cursor: pointer;
  list-style: none;
  user-select: none;
}
.alice-ins__rec-head::-webkit-details-marker {
  display: none;
}
.alice-ins__rec-head:hover {
  background: var(--bg-muted);
}
.alice-ins__rec-mark {
  width: 8px;
  height: 8px;
  background: var(--vp-rood-100);
  flex-shrink: 0;
}
.alice-ins__rec-title {
  flex: 1;
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-ins__rec-count {
  padding: 3px 8px;
  border: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--fg-3);
}
.alice-ins__rec-body {
  padding: 16px 16px 20px;
  border-top: 1px solid var(--border-hairline);
  background: var(--bg-muted);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ============== Responsive ============== */
@media (max-width: 1279px) {
  .alice-ins__head {
    padding: 24px 20px 20px;
    flex-direction: column;
    gap: 16px;
  }
  .alice-ins__head-actions {
    align-self: flex-start;
  }
  .alice-ins__stats {
    grid-template-columns: 1fr;
  }
  .alice-ins__stat-tile + .alice-ins__stat-tile {
    border-left: none;
    border-top: 1px solid var(--border-hairline);
  }
  .alice-ins__chapters {
    padding: 16px 20px 32px;
  }
}
</style>
